/**
 * Text Component
 *
 * A text element with support for size, weight, color, and alignment.
 * Styling is class-driven from ui-core recipe generated CSS.
 *
 * @module @smoothie-framework/ui/components
 *
 * @example
 * ```tsx
 * <Text size="md" weight="normal">Hello World</Text>
 *
 * <Text size="lg" align="center">
 *   Centered large text
 * </Text>
 *
 * <Text size="sm" color="subtle">Secondary text</Text>
 * ```
 */
import { createNodeId } from '../../render/node-id';
import { buildUtilityClass, hasFlatThemeColorAlias, resolveResponsiveValue } from '@smoothie-framework/ui-styles/styling';
import { buildComponentClassNames, resolveResponsivePropValue } from '../../authoring/internal';
import { extensionIndex } from '../../authoring/extension-registry';
import { extractStaticText, normalizeChildrenToNodes } from '../../internal/children';
import { mergeStyleStrings } from '../../internal/style';
import { getTheme } from '../../theme/theme_context';
import { composeAsChildNode } from '../../authoring/as-child';
import { buildCommonUIProps } from '../../authoring/common-props';
import { resolveCanonicalUtilityProps, } from '../../authoring/utility-props';
function usesTextColorClass(value) {
    const builtInColors = new Set([
        'default',
        'subtle',
        'disabled',
        'inverse',
        'accent',
        'error',
        'success',
        'warning',
    ]);
    if (builtInColors.has(value)) {
        return true;
    }
    const extensionVariants = extensionIndex.mergeRecipeExtensions('Text').variants.color ?? {};
    return Object.prototype.hasOwnProperty.call(extensionVariants, value);
}
export function Text(props) {
    const { id, as: asProp, asChild, textStyle, size = 'md', weight = 'normal', color = 'default', align = 'left', truncate, uppercase, maxLines, grow, hideFrom, hideBelow, style, className, children, 'data-ui': dataUi, ...utilityPropsInput } = props;
    const as = asProp ?? 'p';
    const theme = getTheme();
    const resolvedSize = resolveResponsivePropValue(size, 'md');
    const resolvedWeight = resolveResponsivePropValue(weight, 'normal');
    const resolvedAlign = resolveResponsivePropValue(align, 'left');
    const effectiveTruncate = maxLines === undefined ? truncate : undefined;
    const resolvedTruncate = resolveOptionalResponsivePropValue(effectiveTruncate) ?? false;
    const resolvedUppercase = resolveOptionalResponsivePropValue(uppercase) ?? false;
    const resolvedMaxLines = resolveOptionalResponsivePropValue(maxLines);
    const baseClass = 'smth-text';
    const usesRecipeColorClass = typeof color === 'string' && usesTextColorClass(color);
    const usesThemeColorUtilityClass = typeof color === 'string'
        && !usesRecipeColorClass
        && hasFlatThemeColorAlias(theme, color);
    const classes = [
        buildComponentClassNames(baseClass, { size, weight, align }, className, { fallbackVariants: { size: 'md', weight: 'normal', align: 'left' } }),
    ];
    if (usesRecipeColorClass) {
        classes.push(`${baseClass}--${color}`);
    }
    else if (usesThemeColorUtilityClass) {
        const utilityClass = buildUtilityClass('color', color);
        if (utilityClass) {
            classes.push(utilityClass);
        }
    }
    const styleParts = [];
    if (typeof color === 'string' && !usesRecipeColorClass && !usesThemeColorUtilityClass) {
        styleParts.push(`color: ${color}`);
    }
    const mergedStyle = mergeStyleStrings(styleParts, style);
    const resolvedGrow = typeof grow === 'boolean' ? (grow ? 1 : 0) : grow;
    const textProps = {
        className: classes.join(' '),
        'data-as': as,
        'data-size': resolvedSize,
        'data-weight': resolvedWeight,
        'data-color': color,
        'data-align': resolvedAlign,
        'data-truncate': resolvedTruncate ? 'true' : 'false',
        'data-uppercase': resolvedUppercase ? 'true' : 'false',
        ...(resolvedMaxLines !== undefined && { 'data-max-lines': String(resolvedMaxLines) }),
        ...(textStyle !== undefined && { textStyle }),
        ...(effectiveTruncate !== undefined && { truncate: effectiveTruncate }),
        ...(uppercase !== undefined && { uppercase }),
        ...(maxLines !== undefined && { maxLines }),
        ...(hideFrom !== undefined && { hideFrom }),
        ...(hideBelow !== undefined && { hideBelow }),
        ...(resolvedGrow !== undefined && { grow: resolvedGrow }),
        ...resolveCanonicalUtilityProps(utilityPropsInput),
        ...buildCommonUIProps({
            id,
            style: mergedStyle,
            dataUi,
            defaultDataUi: 'Text',
        }),
    };
    if (asChild) {
        return composeAsChildNode({
            componentType: 'Text',
            props: textProps,
            children,
            explicitDataUi: dataUi,
            semanticTagOverride: asProp,
        });
    }
    const childNodes = normalizeChildrenToNodes(children);
    const textContent = extractStaticText(childNodes);
    return {
        id: createNodeId(),
        type: 'Text',
        props: textProps,
        children: textContent === undefined ? childNodes : [],
        text: textContent,
        _semanticTag: as,
    };
}
function resolveOptionalResponsivePropValue(value) {
    return resolveResponsiveValue(value, undefined, getTheme());
}
//# sourceMappingURL=Text.js.map